import {
  type Experiments,
  type ExperimentVariants,
} from '@hungryroot/experiments';
import { logUnknownErrorResponse } from '@hungryroot/o11y';
import { type OccasionPreferenceDietaryRestrictions } from '@hungryroot/rest';
import { type GetTagGroupResponse } from '@hungryroot/rest/api/get_tag_group/get';
import { tagGroups, useApiV1TagGroup } from '@hungryroot/tags';
import { useGetOccasionPreferences } from './useGetOccasionPreferences';

export type MealTypePreferences = number[] | null;

function getMealTypePreferenceByTag(
  tagGroupResponse?: GetTagGroupResponse
): MealTypePreferences {
  if (tagGroupResponse === undefined) {
    return null;
  }

  return tagGroupResponse.tags[0].map((tag) => tag.id);
}

// Fetch options for meal type quiz pages using tags and occasion preferences
export function useGetMealTypePreferences<T extends Experiments>(
  dietaryRestriction?: OccasionPreferenceDietaryRestrictions,
  variant?: ExperimentVariants<T>
):
  | {
      breakfastOptionsById: MealTypePreferences;
      lunchOptionsById: MealTypePreferences;
      dinnerOptionsById: MealTypePreferences;
      snacksOptionsById: MealTypePreferences;
      sweetsOptionsById: MealTypePreferences;
    }
  | undefined {
  const isVariant = variant !== undefined && variant > 0;

  // Using deprecated v1 tag hook to keep control data fetching consistent. Consider updating during exp removal if control wins
  const { data: breakfastTagGroupData } = useApiV1TagGroup(
    { tagGroupId: tagGroups.breakfast },
    {
      enabled: !isVariant,
    }
  );
  const { data: lunchDishTypeTagGroup } = useApiV1TagGroup(
    { tagGroupId: tagGroups.LunchDishTypes },
    {
      enabled: !isVariant,
    }
  );
  const { data: dinnerDishTypeTagGroup } = useApiV1TagGroup(
    { tagGroupId: tagGroups.DinnerDishTypes },
    {
      enabled: !isVariant,
    }
  );
  const { data: snacksTagGroup } = useApiV1TagGroup(
    { tagGroupId: tagGroups.snack },
    {
      enabled: !isVariant,
    }
  );
  const { data: sweetsTagGroup } = useApiV1TagGroup(
    { tagGroupId: tagGroups.sweets },
    {
      enabled: !isVariant,
    }
  );

  const dietaryRestrictionParam = isVariant ? dietaryRestriction : undefined;

  const {
    data: occassionPreferenceOptions,
    isFetching: isFetchingOccassionPreferenceOptions,
    isError: isErrorOccasionPreferenceOptions,
  } = useGetOccasionPreferences(dietaryRestrictionParam);

  if (variant === undefined) {
    return;
  }

  if (variant > 0) {
    if (isFetchingOccassionPreferenceOptions) {
      return;
    }

    if (occassionPreferenceOptions === undefined) {
      if (isErrorOccasionPreferenceOptions) {
        logUnknownErrorResponse('Failed to fetch occasion preference options', {
          key: 'quiz.occasionPreferences.fetch',
          dietaryRestriction,
          occassionPreferenceOptions,
        });
      }

      return {
        breakfastOptionsById: [],
        lunchOptionsById: [],
        dinnerOptionsById: [],
        snacksOptionsById: [],
        sweetsOptionsById: [],
      };
    }

    return {
      breakfastOptionsById: occassionPreferenceOptions.breakfast.map(
        (option) => option.object_id
      ),
      lunchOptionsById: occassionPreferenceOptions.lunch.map(
        (option) => option.object_id
      ),
      dinnerOptionsById: occassionPreferenceOptions.dinner.map(
        (option) => option.object_id
      ),
      snacksOptionsById: occassionPreferenceOptions.snack.map(
        (option) => option.object_id
      ),
      sweetsOptionsById: occassionPreferenceOptions.sweet.map(
        (option) => option.object_id
      ),
    };
  }

  return {
    breakfastOptionsById: getMealTypePreferenceByTag(breakfastTagGroupData),
    lunchOptionsById: getMealTypePreferenceByTag(lunchDishTypeTagGroup),
    dinnerOptionsById: getMealTypePreferenceByTag(dinnerDishTypeTagGroup),
    snacksOptionsById: getMealTypePreferenceByTag(snacksTagGroup),
    sweetsOptionsById: getMealTypePreferenceByTag(sweetsTagGroup),
  };
}
